import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import {
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { getCustomerHourReport } from "../../api/api";
import { getCurrentUser } from "../../utils/auth";

// Styled table components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: "bold",
        textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: "center",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const timeSlots = [
    "06:00-08:00",
    "08:00-10:00",
    "10:00-12:00",
    "12:00-14:00",
    "14:00-16:00",
    "16:00-18:00",
    "18:00-20:00",
];

const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const Customer_Hour_Report = ({ startDate, endDate }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const chartRef = useRef(null);
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    // Fetch from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const user = getCurrentUser();
                setCurrentUser(user);

                const startDateParam = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const endDateParam = endDate ? endDate.format("YYYY-MM-DD") : undefined;
                const response = await getCustomerHourReport(startDateParam, endDateParam);
                if (response.success) {
                    setCustomerData(response.data);
                } else {
                    console.error("Failed to fetch data:", response.message);
                }
                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch data:", err);
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    // Draw chart when data is ready
    useEffect(() => {
        if (!customerData) return;
        const chartInstance = echarts.init(chartRef.current);

        const seriesData = days.map((day) => ({
            name: day,
            type: "bar",
            data: customerData[day],
            label: {
                show: !isMobile,
                position: "top",
                fontSize: 10,
            },
        }));

        const options = {
            title: {
                text: `Average Customer Count (2-Hour Time Slots)${
                    currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""
                }`,
                left: "center",
                textStyle: { fontSize: 16, fontWeight: "bold" },
            },
            tooltip: { trigger: "axis" },
            legend: {
                type: isMobile ? "scroll" : "plain",
                bottom: 0,
            },
            grid: {
                left: "3%",
                right: "4%",
                bottom: isMobile ? 80 : 50,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: timeSlots,
                axisLabel: {
                    rotate: isMobile ? 45 : 30,
                    fontSize: isMobile ? 10 : 12,
                },
            },
            yAxis: {
                type: "value",
                name: "Customers",
            },
            series: seriesData,
            barWidth: isMobile ? "35%" : "50%",
            colorBy: "series",
        };

        chartInstance.setOption(options);
        const handleResize = () => chartInstance.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            chartInstance.dispose();
            window.removeEventListener("resize", handleResize);
        };
    }, [customerData, isMobile]);

    return (
        <Box sx={{ p: 3 }}>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Typography
                        variant="h6"
                        align="center"
                        sx={{ fontWeight: "bold", mb: 2 }}
                    >
                        Average Customer Count by Time Slot
                        {currentUser?.branch_name
                            ? ` - ${currentUser.branch_name}`
                            : ""}
                    </Typography>

                    <TableContainer
                        component={Paper}
                        sx={{ mb: 4, overflowX: "auto" }}
                    >
                        <Table
                            sx={{ minWidth: 800 }}
                            aria-label="Customer Hour Report Table"
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>
                                        Day / Time Slot
                                    </StyledTableCell>
                                    {timeSlots.map((slot) => (
                                        <StyledTableCell key={slot}>
                                            {slot}
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {days.map((day) => (
                                    <StyledTableRow key={day}>
                                        <StyledTableCell align="center">
                                            {day}
                                        </StyledTableCell>
                                        {customerData[day].map((count, idx) => (
                                            <StyledTableCell key={idx}>
                                                {count}
                                            </StyledTableCell>
                                        ))}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                        Customer Distribution by 2-Hour Time Slots
                    </Typography>

                    <Box sx={{ overflowX: "auto", mb: 4 }}>
                        <Box
                            ref={chartRef}
                            sx={{
                                minWidth: isMobile ? 900 : "100%",
                                height: 400,
                                borderRadius: 2,
                                backgroundColor: "#fff",
                                boxShadow: 2,
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default Customer_Hour_Report;
